import { useState, useEffect, useCallback, useRef } from 'react';
import { useFetcher, useRevalidator } from '@remix-run/react';
import { useFeature } from '@modules/root/hook/use-feature';
import { useUser } from '~/modules/user';
import type { PageBuilderUpscaleImageAction } from '~/routes/remix-api.page-builder.upscale-image';
import type { PageBuilderUpscaleComparisonAction } from '~/routes/remix-api.page-builder.upscale-comparison';
import { UPSCALE_COMPARISON_API_ROUTE } from '~/routes/remix-api.page-builder.upscale-comparison';

type BoostState =
    | 'idle'
    | 'upscaling'
    | 'comparing'
    | 'reverting'
    | 'fetching-comparison';

interface UseExistImageBoostParams {
    /** The LibraryImage ID */
    libraryImageId: number;
    /** The current image URL displayed on the page */
    currentImageUrl: string;
    /** Expected display width */
    expectedWidth: number;
    /** Expected display height */
    expectedHeight: number;
    /** Optional ImageColumn ID — needed for page builder module images so the column reference is updated after upscale */
    columnId?: string | number;
    /** Callback when boost completes and user keeps the image */
    onKeepNewImage?: () => void;
    /** Callback when user reverts */
    onRevert?: () => void;
    /** Called before any internal revalidation so the parent can preserve carousel position */
    onBeforeRevalidate?: (params: { preserveLibraryImageId: number }) => void;
}

/** Optimistic overrides applied immediately after clicking "Revert to Original" */
interface RevertOverrides {
    imageUrl: string;
    libraryImageId: number;
}

interface UseExistImageBoostReturn {
    /** Current state of the boost workflow */
    boostState: BoostState;
    /** Trigger the upscale */
    handleBoostClick: (params?: {
        libraryImageId?: number;
        currentImageUrl?: string;
    }) => void;
    /** Keep the new (upscaled) image — image already replaced in DB during upscale */
    handleKeepNewImage: () => void;
    /** Revert to the old image */
    handleRevertToOldImage: () => void;
    /** Fetch the original image URL and display the comparison for an already-upscaled image */
    handleViewComparison: () => void;
    /** The original image URL (captured before upscaling) */
    originalImageUrl: string | null;
    /** The upscaled image URL (available after upscale completes) */
    upscaledImageUrl: string | null;
    /** Error message if upscale or revert failed */
    error: string | null;
    /** Whether the boost button should be shown (staff + feature flag) */
    canBoost: boolean;
    /** Optimistic overrides active after revert — null once revalidation catches up */
    revertOverrides: RevertOverrides | null;
    /** Whether the original image meets the minimum dimension requirements for revert */
    canRevert: boolean;
}

interface UpscaleApiResponse {
    success: boolean;
    error?: string;
    data?: Record<string, unknown>;
}

/** Interval between revalidation retries when the URL hasn't changed after upscale */
const REVALIDATION_RETRY_INTERVAL_MS = 5_000;
/** Maximum revalidation retries before giving up (6 × 5s = 30s after API returns) */
const MAX_REVALIDATION_RETRIES = 6;
/** Hard timeout for the entire upscale workflow (API call + revalidation) */
const UPSCALE_TIMEOUT_MS = 90_000;

/**
 * Hook managing the boost lifecycle for existing images on detail pages.
 *
 * State machine:
 *   idle → (click Boost) → upscaling → (API returns, revalidate) → comparing
 *   idle → (click Resolution Boosted) → fetching-comparison → comparing
 *   comparing → (click Keep) → idle (image already replaced)
 *   comparing → (click Revert) → idle (optimistic, API fires in background)
 *   upscaling → (API error or timeout) → idle
 *   fetching-comparison → (API error) → idle
 */
export const useExistImageBoost = ({
    libraryImageId,
    currentImageUrl,
    expectedWidth,
    expectedHeight,
    columnId,
    onKeepNewImage,
    onRevert,
    onBeforeRevalidate,
}: UseExistImageBoostParams): UseExistImageBoostReturn => {
    const [boostState, setBoostState] = useState<BoostState>('idle');
    const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(
        null,
    );
    const [upscaledImageUrl, setUpscaledImageUrl] = useState<string | null>(
        null,
    );
    const [error, setError] = useState<string | null>(null);
    const [revertOverrides, setRevertOverrides] =
        useState<RevertOverrides | null>(null);
    /** Whether the original image meets the minimum dimension requirements for revert */
    const [canRevert, setCanRevert] = useState(true);
    // Refs to capture image data at click time (prevents stale closure if slide changes during upscale)
    const boostedImageUrlRef = useRef<string | null>(null);
    const boostedImageIdRef = useRef<number>(0);
    /** The new LibraryImage ID returned by upscale (current image while comparing) */
    const upscaledImageIdRef = useRef<number>(0);
    /** True while waiting for revalidation to deliver the new image URL */
    const awaitingNewUrlRef = useRef(false);
    /** Stores the original image ID so the revert response handler can call onBeforeRevalidate */
    const revertOriginalImageIdRef = useRef<number>(0);
    /** Interval timer for periodic revalidation retries when URL doesn't change after upscale */
    const retryTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const retryCountRef = useRef(0);
    /** Hard timeout covering the entire upscale workflow (API + revalidation) */
    const upscaleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );

    const upscaleFetcher = useFetcher<PageBuilderUpscaleImageAction>();
    const revertFetcher = useFetcher<UpscaleApiResponse>();
    const comparisonFetcher = useFetcher<PageBuilderUpscaleComparisonAction>();
    const revalidator = useRevalidator();

    const user = useUser();
    const isAPStaff = user.__typename === 'Me' && user.IsArchiproStaff;
    const featureFlagEnabled = useFeature('image_auto_upscale');
    const canBoost = isAPStaff && featureFlagEnabled;

    const clearUpscaleTimers = useCallback(() => {
        if (retryTimerRef.current) {
            clearInterval(retryTimerRef.current);
            retryTimerRef.current = null;
        }
        retryCountRef.current = 0;
        if (upscaleTimeoutRef.current) {
            clearTimeout(upscaleTimeoutRef.current);
            upscaleTimeoutRef.current = null;
        }
    }, []);

    // Clean up all timers on unmount
    useEffect(() => clearUpscaleTimers, [clearUpscaleTimers]);

    // Handle upscale response: trigger revalidation to get the new image URL.
    // Also starts a retry interval to periodically re-revalidate in case the
    // URL doesn't change on the first attempt (e.g. CDN / edge caching).
    useEffect(() => {
        if (upscaleFetcher.state !== 'idle' || !upscaleFetcher.data) return;

        if (upscaleFetcher.data.success) {
            setError(null);
            awaitingNewUrlRef.current = true;
            upscaledImageIdRef.current = Number(
                upscaleFetcher.data?.data?.newLibraryImageID,
            );
            const preserveLibraryImageId =
                upscaledImageIdRef.current ||
                boostedImageIdRef.current ||
                libraryImageId;
            onBeforeRevalidate?.({ preserveLibraryImageId });
            revalidator.revalidate();

            // Start retry interval for when the first revalidation
            // doesn't produce a new URL (CDN/edge caching, etc.)
            clearUpscaleTimers();
            retryTimerRef.current = setInterval(() => {
                retryCountRef.current += 1;

                if (!awaitingNewUrlRef.current) {
                    clearUpscaleTimers();
                    return;
                }

                if (retryCountRef.current >= MAX_REVALIDATION_RETRIES) {
                    clearUpscaleTimers();
                    awaitingNewUrlRef.current = false;
                    setError(
                        'Boost completed but the updated image could not ' +
                            'be loaded. Please refresh the page.',
                    );
                    setBoostState('idle');
                    return;
                }

                revalidator.revalidate();
            }, REVALIDATION_RETRY_INTERVAL_MS);
        } else {
            const message =
                upscaleFetcher.data.error || 'Failed to upscale image';
            setError(message);
            setBoostState('idle');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [upscaleFetcher.state, upscaleFetcher.data]);

    // After revalidation, the loader returns updated data and currentImageUrl changes.
    // Detect URL change to transition from upscaling → comparing.
    // Guard: only transition when the incoming props still refer to the boosted
    // image (or its replacement). This prevents a false positive when the parent
    // carousel advances to a different image during the upscale.
    useEffect(() => {
        if (!awaitingNewUrlRef.current || !currentImageUrl) return;

        const capturedOriginalUrl = boostedImageUrlRef.current;

        if (boostState === 'upscaling') {
            const isRelevantImage =
                libraryImageId === boostedImageIdRef.current ||
                (upscaledImageIdRef.current > 0 &&
                    libraryImageId === upscaledImageIdRef.current);

            if (isRelevantImage && currentImageUrl !== capturedOriginalUrl) {
                awaitingNewUrlRef.current = false;
                clearUpscaleTimers();
                setUpscaledImageUrl(currentImageUrl);
                setBoostState('comparing');
            }
        }
    }, [boostState, currentImageUrl, libraryImageId, clearUpscaleTimers]);

    // Clear optimistic revert overrides once the loader delivers the original values
    useEffect(() => {
        if (!revertOverrides) return;
        const loaderMatchesOriginal =
            currentImageUrl === revertOverrides.imageUrl ||
            libraryImageId === revertOverrides.libraryImageId;
        if (loaderMatchesOriginal) {
            setRevertOverrides(null);
        }
    }, [currentImageUrl, libraryImageId, revertOverrides]);

    // Handle revert response — state already reset optimistically in handleRevertToOldImage
    useEffect(() => {
        if (revertFetcher.state !== 'idle' || !revertFetcher.data) return;

        if (revertFetcher.data.success) {
            const originalImageId = revertOriginalImageIdRef.current;
            if (originalImageId) {
                onBeforeRevalidate?.({
                    preserveLibraryImageId: originalImageId,
                });
            }
            revalidator.revalidate();
            revertOriginalImageIdRef.current = 0;
        } else {
            const message =
                revertFetcher.data.error || 'Failed to revert image';
            setError(message);
            setRevertOverrides(null);
            revertOriginalImageIdRef.current = 0;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revertFetcher.state, revertFetcher.data]);

    // Handle comparison fetch response — transitions to comparing state
    useEffect(() => {
        if (comparisonFetcher.state !== 'idle' || !comparisonFetcher.data)
            return;
        if (boostState !== 'fetching-comparison') return;

        const data = comparisonFetcher.data;
        if (data.success && data.originalImageUrl) {
            setOriginalImageUrl(data.originalImageUrl);
            setUpscaledImageUrl(currentImageUrl);
            boostedImageIdRef.current = data.originalLibraryImageId ?? 0;
            boostedImageUrlRef.current = data.originalImageUrl;
            const origW = data.originalWidth ?? 0;
            const origH = data.originalHeight ?? 0;
            setCanRevert(origW >= expectedWidth && origH >= expectedHeight);
            setError(null);
            setBoostState('comparing');
        } else {
            const message = data.error || 'Failed to load comparison data';
            setError(message);
            setBoostState('idle');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [comparisonFetcher.state, comparisonFetcher.data]);

    const handleBoostClick = useCallback(
        (params?: { libraryImageId?: number; currentImageUrl?: string }) => {
            const targetLibraryImageId =
                params?.libraryImageId ?? libraryImageId;
            const targetCurrentImageUrl =
                params?.currentImageUrl ?? currentImageUrl;
            if (boostState !== 'idle' || !targetLibraryImageId) return;

            setOriginalImageUrl(targetCurrentImageUrl);
            boostedImageUrlRef.current = targetCurrentImageUrl;
            boostedImageIdRef.current = targetLibraryImageId;
            upscaledImageIdRef.current = 0;
            setError(null);
            setBoostState('upscaling');

            // Hard timeout: if the entire workflow hasn't resolved, cancel
            clearUpscaleTimers();
            upscaleTimeoutRef.current = setTimeout(() => {
                clearUpscaleTimers();
                awaitingNewUrlRef.current = false;
                setError('Image boost timed out. Please try again.');
                setBoostState('idle');
            }, UPSCALE_TIMEOUT_MS);

            const formData = new FormData();
            formData.set('libraryImageId', targetLibraryImageId.toString());
            formData.set('expectedWidth', expectedWidth.toString());
            formData.set('expectedHeight', expectedHeight.toString());
            formData.set('forceUpscale', 'on');
            if (columnId) {
                formData.set('columnId', columnId.toString());
            }

            upscaleFetcher.submit(formData, {
                method: 'post',
                action: '/remix-api/page-builder/upscale-image',
            });
        },
        [
            boostState,
            libraryImageId,
            currentImageUrl,
            expectedWidth,
            expectedHeight,
            columnId,
            upscaleFetcher,
            clearUpscaleTimers,
        ],
    );

    const handleKeepNewImage = useCallback(() => {
        clearUpscaleTimers();
        setBoostState('idle');
        setOriginalImageUrl(null);
        setUpscaledImageUrl(null);
        setCanRevert(true);
        boostedImageUrlRef.current = null;
        boostedImageIdRef.current = 0;
        upscaledImageIdRef.current = 0;
        onKeepNewImage?.();
    }, [onKeepNewImage, clearUpscaleTimers]);

    const handleRevertToOldImage = useCallback(() => {
        if (!originalImageUrl) return;
        clearUpscaleTimers();

        const imageId = upscaledImageIdRef.current || libraryImageId;
        const capturedOriginalId = boostedImageIdRef.current;
        revertOriginalImageIdRef.current = capturedOriginalId;

        // Optimistic: immediately close comparison, show old image, and reset state
        setRevertOverrides({
            imageUrl: originalImageUrl,
            libraryImageId: capturedOriginalId,
        });
        setBoostState('idle');
        setError(null);
        setOriginalImageUrl(null);
        setUpscaledImageUrl(null);
        boostedImageUrlRef.current = null;
        boostedImageIdRef.current = 0;
        upscaledImageIdRef.current = 0;
        onRevert?.();

        const formData = new FormData();
        formData.set('libraryImageId', imageId.toString());
        formData.set('revertUpscale', 'on');
        if (columnId) {
            formData.set('columnId', columnId.toString());
        }

        revertFetcher.submit(formData, {
            method: 'POST',
            action: '/remix-api/page-builder/upscale-image',
        });
    }, [
        libraryImageId,
        originalImageUrl,
        columnId,
        revertFetcher,
        onRevert,
        clearUpscaleTimers,
    ]);

    /** Fetch the original image URL to re-display comparison for an already-upscaled image */
    const handleViewComparison = useCallback(() => {
        if (boostState !== 'idle' || !libraryImageId || !currentImageUrl)
            return;

        setError(null);
        setBoostState('fetching-comparison');

        const formData = new FormData();
        formData.set('libraryImageId', libraryImageId.toString());
        formData.set('currentImageUrl', currentImageUrl);

        comparisonFetcher.submit(formData, {
            method: 'POST',
            action: UPSCALE_COMPARISON_API_ROUTE,
        });
    }, [boostState, libraryImageId, currentImageUrl, comparisonFetcher]);

    return {
        boostState,
        handleBoostClick,
        handleKeepNewImage,
        handleRevertToOldImage,
        handleViewComparison,
        originalImageUrl,
        upscaledImageUrl,
        error,
        canBoost,
        revertOverrides,
        canRevert,
    };
};
